// Approval form builder — visual field palette + canvas + property editor.
import { useEffect, useState } from "react";
import { api } from "../../../shared/api/client";
import { Empty, Loading } from "../../../shared/components/Empty";
import { Pill } from "../../../shared/components/Pill";
import { toast } from "../../../shared/stores/toasts";

type FieldType = "text" | "textarea" | "number" | "date" | "select" | "attachment";

type Field = {
  id: string;
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: string[];    // for SELECT type
  placeholder?: string;
};

type Form = {
  id: number;
  code: string;
  name: string;
  category: string;
  description: string | null;
  template_json: { fields: Field[] };
  is_active: boolean;
  version: number;
};

const FIELD_TYPES: { type: FieldType; label: string; icon: string }[] = [
  { type: "text",       label: "텍스트",    icon: "T" },
  { type: "textarea",   label: "長문(여러줄)", icon: "¶" },
  { type: "number",     label: "숫자",      icon: "#" },
  { type: "date",       label: "날짜",      icon: "📅" },
  { type: "select",     label: "선택",      icon: "▾" },
  { type: "attachment", label: "첨부파일",  icon: "📎" },
];

const CATEGORIES = ["HR", "FINANCE", "GENERAL", "CONTRACT", "ATTENDANCE"];

function makeId() { return `f${Date.now().toString(36)}`; }

function FormPreview({ fields, formName, desc }: { fields: Field[]; formName: string; desc?: string }) {
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{formName || "미리보기"}</div>
      {desc && <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 10 }}>{desc}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
        {fields.length === 0 && <div style={{ fontSize: 12, color: "var(--text-3)" }}>필드가 없습니다</div>}
        {fields.map((f) => (
          <div key={f.id}>
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 3 }}>
              {f.label}{f.required && <span style={{ color: "#ef4444" }}> *</span>}
            </label>
            {f.type === "textarea" ? <textarea className="t-input" rows={2} style={{ fontSize: 12, width: "100%" }} disabled placeholder={f.placeholder} />
              : f.type === "select" ? <select className="t-input" style={{ fontSize: 12, width: "100%" }} disabled><option>선택…</option>{(f.options ?? []).map((o) => <option key={o}>{o}</option>)}</select>
              : f.type === "attachment" ? <div style={{ fontSize: 11.5, color: "var(--text-3)", padding: "6px 0" }}>📎 파일 첨부</div>
              : <input className="t-input" type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} style={{ fontSize: 12, width: "100%" }} disabled placeholder={f.placeholder} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminApprovalForms() {
  const [forms, setForms] = useState<Form[] | null>(null);
  const [active, setActive] = useState<Form | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [selected, setSelected] = useState<Field | null>(null);
  const [saving, setSaving] = useState(false);
  const [newForm, setNewForm] = useState(false);
  const [draft, setDraft] = useState({ code: "", name: "", category: "HR", description: "" });
  const [meta, setMeta] = useState({ name: "", description: "", is_active: true });
  const [rightTab, setRightTab] = useState<"props" | "preview">("props");

  const load = () =>
    api.get<Form[]>("/approval/forms")
      .then((r) => {
        setForms(r.data);
        if (!active && r.data.length > 0) pick(r.data[0]);
      })
      .catch(() => setForms([]));

  useEffect(() => { load(); }, []);

  const pick = (f: Form) => {
    setActive(f);
    setFields(f.template_json?.fields ?? []);
    setSelected(null);
    setMeta({ name: f.name, description: f.description ?? "", is_active: f.is_active });
  };

  const addField = (type: FieldType) => {
    const f: Field = {
      id: makeId(),
      name: type + "_" + Date.now().toString(36).slice(-4),
      label: FIELD_TYPES.find((t) => t.type === type)!.label,
      type,
      required: false,
      options: type === "select" ? ["옵션 1", "옵션 2"] : undefined,
    };
    setFields((prev) => [...prev, f]);
    setSelected(f);
  };

  const updateField = (id: string, patch: Partial<Field>) => {
    setFields((prev) => prev.map((f) => f.id === id ? { ...f, ...patch } : f));
    setSelected((prev) => prev?.id === id ? { ...prev, ...patch } : prev);
  };

  const deleteField = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    setSelected((prev) => (prev?.id === id ? null : prev));
  };

  const moveField = (id: string, dir: -1 | 1) => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.patch(`/approval/forms/${active.code}`, { template_json: { fields }, name: meta.name, description: meta.description, is_active: meta.is_active });
      toast.success("양식 저장됨", meta.name);
      load();
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message);
    } finally {
      setSaving(false);
    }
  };

  const createForm = async () => {
    if (!draft.code || !draft.name) return;
    setSaving(true);
    try {
      const res = await api.post<Form>("/approval/forms", {
        ...draft,
        template_json: { fields: [] },
      });
      toast.success("양식 생성됨");
      setNewForm(false);
      setDraft({ code: "", name: "", category: "HR", description: "" });
      setForms((prev) => [...(prev ?? []), res.data]);
      pick(res.data);
    } catch (e: any) {
      toast.error("생성 실패", e?.response?.data?.error?.message);
    } finally {
      setSaving(false);
    }
  };

  if (forms === null) return <Loading />;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12, padding: 16, height: "calc(100vh - 130px)" }}>
      {/* Left — form list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>결재 양식</span>
          <button className="t-btn solid" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => setNewForm((v) => !v)}>
            {newForm ? "취소" : "+ 새 양식"}
          </button>
        </div>

        {newForm && (
          <div className="t-card" style={{ padding: 10, display: "grid", gap: 6 }}>
            <input className="t-input" placeholder="코드 (영문대문자)" value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} style={{ fontSize: 12 }} />
            <input className="t-input" placeholder="양식 이름" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={{ fontSize: 12 }} />
            <select className="t-input" value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })} style={{ fontSize: 12 }}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button className="t-btn solid" style={{ fontSize: 11 }} onClick={createForm} disabled={saving || !draft.code || !draft.name}>
              생성
            </button>
          </div>
        )}

        {forms.length === 0 && <Empty msg="등록된 양식 없음" />}
        {forms.map((f) => (
          <button key={f.id}
            className={`t-subnav-item ${active?.id === f.id ? "active" : ""}`}
            onClick={() => pick(f)}
            style={{ textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
          >
            <span style={{ fontWeight: 600, fontSize: 12 }}>{f.name}</span>
            <span style={{ fontSize: 10, color: "var(--text-3)" }}>{f.code} · v{f.version}</span>
          </button>
        ))}
      </div>

      {/* Right — builder (palette + canvas + props) */}
      {active ? (
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 220px", gap: 10, overflow: "hidden" }}>

          {/* Field palette */}
          <div className="t-card" style={{ padding: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: "var(--text-3)" }}>필드 팔레트</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {FIELD_TYPES.map((t) => (
                <button key={t.type} className="t-btn" style={{ fontSize: 12, justifyContent: "flex-start", gap: 8 }}
                  onClick={() => addField(t.type)}>
                  <span style={{ width: 18, textAlign: "center" }}>{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="t-card" style={{ padding: 10, overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <input className="t-input" style={{ fontSize: 14, fontWeight: 700, flex: 1, minWidth: 160 }} value={meta.name}
                onChange={(e) => setMeta({ ...meta, name: e.target.value })} placeholder="양식 이름" />
              <Pill kind="info">{active.category}</Pill>
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-2)", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={meta.is_active} onChange={(e) => setMeta({ ...meta, is_active: e.target.checked })} /> 사용
              </label>
              <button className="t-btn t-btn-primary" style={{ fontSize: 12 }} onClick={save} disabled={saving}>
                {saving ? "저장 중…" : "저장"}
              </button>
            </div>
            <input className="t-input" style={{ fontSize: 12, width: "100%", marginBottom: 10 }} placeholder="양식 설명 (선택)"
              value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} />

            {fields.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--text-3)", fontSize: 12, padding: 40 }}>
                ← 팔레트에서 필드를 추가하세요
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {fields.map((f, idx) => (
                <div key={f.id}
                  onClick={() => setSelected(f)}
                  style={{
                    border: `1px solid ${selected?.id === f.id ? "var(--p-500)" : "var(--border)"}`,
                    borderRadius: 8, padding: "8px 10px", cursor: "pointer",
                    background: selected?.id === f.id ? "var(--panel)" : undefined,
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--text-3)", width: 16, textAlign: "center" }}>
                    {FIELD_TYPES.find((t) => t.type === f.type)?.icon}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</span>
                    <span style={{ fontSize: 10, color: "var(--text-3)", marginLeft: 6 }}>{f.name}</span>
                  </div>
                  <Pill kind={f.required ? "danger" : "muted"}>
                    {f.required ? "필수" : "선택"}
                  </Pill>
                  <div style={{ display: "flex", gap: 2 }}>
                    <button className="t-btn" style={{ fontSize: 10, padding: "1px 6px" }}
                      onClick={(e) => { e.stopPropagation(); moveField(f.id, -1); }} disabled={idx === 0}>↑</button>
                    <button className="t-btn" style={{ fontSize: 10, padding: "1px 6px" }}
                      onClick={(e) => { e.stopPropagation(); moveField(f.id, 1); }} disabled={idx === fields.length - 1}>↓</button>
                    <button className="t-btn" style={{ fontSize: 10, padding: "1px 6px", color: "var(--danger, #ef4444)" }}
                      onClick={(e) => { e.stopPropagation(); deleteField(f.id); }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Properties panel */}
          <div className="t-card" style={{ padding: 10, overflow: "auto" }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
              {(["props", "preview"] as const).map((t) => (
                <button key={t} className={`t-btn ${rightTab === t ? "t-btn-primary" : ""}`} style={{ fontSize: 11.5, flex: 1, padding: "4px 0" }} onClick={() => setRightTab(t)}>
                  {t === "props" ? "속성" : "미리보기"}
                </button>
              ))}
            </div>
            {rightTab === "preview" ? (
              <FormPreview fields={fields} formName={meta.name} desc={meta.description} />
            ) : selected ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label className="t-label">레이블</label>
                  <input className="t-input" style={{ fontSize: 12 }} value={selected.label}
                    onChange={(e) => updateField(selected.id, { label: e.target.value })} />
                </div>
                <div>
                  <label className="t-label">필드명 (영문)</label>
                  <input className="t-input" style={{ fontSize: 12 }} value={selected.name}
                    onChange={(e) => updateField(selected.id, { name: e.target.value })} />
                </div>
                <div>
                  <label className="t-label">타입</label>
                  <select className="t-input" style={{ fontSize: 12 }} value={selected.type}
                    onChange={(e) => updateField(selected.id, { type: e.target.value as FieldType })}>
                    {FIELD_TYPES.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" id="req" checked={selected.required}
                    onChange={(e) => updateField(selected.id, { required: e.target.checked })} />
                  <label htmlFor="req" style={{ fontSize: 12 }}>필수 입력</label>
                </div>
                {selected.type === "select" && (
                  <div>
                    <label className="t-label">선택지 (줄바꿈으로 구분)</label>
                    <textarea className="t-input" rows={5} style={{ fontSize: 12, resize: "vertical" }}
                      value={(selected.options ?? []).join("\n")}
                      onChange={(e) => updateField(selected.id, { options: e.target.value.split("\n").filter(Boolean) })} />
                  </div>
                )}
                <button className="t-btn" style={{ fontSize: 11, color: "var(--danger, #ef4444)", marginTop: 8 }}
                  onClick={() => deleteField(selected.id)}>
                  이 필드 삭제
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>필드를 클릭하면 속성을 편집합니다</div>
            )}
          </div>
        </div>
      ) : (
        <Empty msg="왼쪽에서 양식을 선택하세요" />
      )}
    </div>
  );
}
